import {
  type THeadingElement,
  type TImageElement,
  type TParagraphElement,
  type TRichTextElement,
  updateMergeTagText,
} from '@shared/dream-components';
import { useMemo } from 'react';
import { getProductTypeLabel } from '~/constants/productTypes';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { Product } from '~/interfaces/product';
import type { StaticPageLoaderData } from '~/services/static_page';
import { serializeNode } from '../serializeNode';

const ALLOWED_MERGE_TAGS = [
  'product.name',
  'product.description',
  'product.details',
  'product.price',
  'product.product_type',
  'product.image',
] as const;

type AllowedMergeTag = (typeof ALLOWED_MERGE_TAGS)[number];

type NodeWithMergeTag = TParagraphElement | TRichTextElement | TImageElement | THeadingElement;

interface UseProductMergeTagProcessorProps {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

interface ProcessedResult {
  node: NodeWithMergeTag;
  children: React.ReactNode;
}

const useProductMergeTagProcessor = ({ node, children }: UseProductMergeTagProcessorProps): ProcessedResult => {
  const { product } = useTypedLoaderData<
    StaticPageLoaderData & {
      product?: Product;
    }
  >();

  return useMemo(() => {
    const mergeTag = node.attrs?.mergeTag;

    // Early return if no merge tag or not an allowed merge tag
    if (!mergeTag || !ALLOWED_MERGE_TAGS.includes(mergeTag as AllowedMergeTag)) {
      return { node, children };
    }

    // Early return if no product present
    if (!product) {
      return { node, children };
    }

    let elementToRender: NodeWithMergeTag = node;

    switch (mergeTag) {
      case 'product.name':
        elementToRender = updateMergeTagText(node, product.name || '') as NodeWithMergeTag;
        break;
      case 'product.description':
        elementToRender = updateMergeTagText(node, product.description || '') as NodeWithMergeTag;
        break;
      case 'product.price':
        elementToRender = updateMergeTagText(node, product.default_product_variant?.price || '') as NodeWithMergeTag;
        break;
      case 'product.product_type':
        elementToRender = updateMergeTagText(node, getProductTypeLabel(product.product_type)) as NodeWithMergeTag;
        break;
      case 'product.details':
        elementToRender = {
          ...node,
          attrs: {
            ...node.attrs,
            tiptapState: product.tiptap_state,
          },
        } as NodeWithMergeTag;
        break;
      case 'product.image':
        elementToRender = {
          ...node,
          attrs: {
            ...node.attrs,
            alt: product.name || '',
            src: product.default_product_variant?.default_image_url || '',
          },
        } as NodeWithMergeTag;
        break;
      default:
        elementToRender = node;
    }

    const childrenToRender = elementToRender.content ? serializeNode(elementToRender.content) : children;

    return {
      node: elementToRender,
      children: childrenToRender,
    };
  }, [node, children, product]);
};

export default useProductMergeTagProcessor;
